import { useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useAuthStore } from "../../store/auth.js";
import { api } from "../../utils/api.js";

export function ApplyConfirmation() {
  const { slug } = useParams<{ slug: string }>();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const location = useLocation();
  const contactEmail = (location.state as { contactEmail?: string } | null)?.contactEmail;
  const displayEmail = contactEmail || user?.email;

  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawn, setWithdrawn] = useState(false);

  const handleWithdraw = async () => {
    setWithdrawing(true);
    try {
      await api.post(`/applications/withdraw/${slug}`);
      setWithdrawn(true);
    } finally {
      setWithdrawing(false);
      setShowWithdraw(false);
    }
  };

  if (withdrawn) {
    return (
      <div className="mx-auto max-w-2xl py-16 text-center">
        <h1 className="text-2xl font-bold text-gray-900">Application Withdrawn</h1>
        <p className="mt-3 text-gray-600">
          Your application has been withdrawn. We appreciate your interest.
        </p>
        <p className="mt-8 text-xs text-gray-400">
          <button onClick={logout} className="text-gray-500 underline hover:text-gray-700">
            Sign out
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl py-16 text-center">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
        <svg
          className="h-8 w-8 text-green-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h1 className="mt-6 text-2xl font-bold text-gray-900">Application Submitted</h1>
      <p className="mt-3 text-gray-600">
        Thank you for applying! Our team typically reviews applications within{" "}
        <strong>5 business days</strong>. We&rsquo;ll be in touch with next steps regardless of the
        outcome.
      </p>
      {displayEmail && (
        <p className="mt-2 text-sm text-gray-500">
          We&rsquo;ve sent a confirmation to <strong>{displayEmail}</strong>. Check your spam folder
          if you don&rsquo;t see it.
        </p>
      )}
      <div className="mt-8 flex items-center justify-center gap-3 text-xs text-gray-400">
        <span>You can safely close this page.</span>
        <button onClick={logout} className="text-gray-500 underline hover:text-gray-700">
          Sign out
        </button>
        <span>&middot;</span>
        {showWithdraw ? (
          <span className="inline-flex items-center gap-2">
            <span className="text-red-600">Withdraw?</span>
            <button
              onClick={handleWithdraw}
              disabled={withdrawing}
              className="text-red-600 underline hover:text-red-800 font-medium"
            >
              {withdrawing ? "..." : "Yes"}
            </button>
            <button
              onClick={() => setShowWithdraw(false)}
              className="text-gray-500 underline hover:text-gray-700"
            >
              No
            </button>
          </span>
        ) : (
          <button
            onClick={() => setShowWithdraw(true)}
            className="text-gray-500 underline hover:text-gray-700"
          >
            Withdraw application
          </button>
        )}
      </div>
    </div>
  );
}
